// Premium CCG route config for: /windsor-to-heathrow
// Keep reusable layout in CcgRoutePremiumPage; keep route-specific SEO/content here.

import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? (
          <ul>
            {card.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        ) : null}
        {card.link ? (
          <Link to={card.link.to} className={card.link.className || "route-link-inline"}>
            {card.link.label}
          </Link>
        ) : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => (
              <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>
                {link.label}
              </Link>
            ))}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const windsorToHeathrowRoute = {
  id: "windsor-to-heathrow",
  path: "/windsor-to-heathrow",
  liveStripVariant: "windsor",
  seo: {
    title: "Windsor to Heathrow Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Windsor and Eton (SL4) to Heathrow. Instant online quote, licensed drivers, flight tracking, 24/7 support and no surge pricing with CheapCabGrab.",
    keywords:
      "Windsor to Heathrow taxi, taxi to Heathrow Airport from Windsor, SL4 taxi to Heathrow, Windsor airport transfer, Eton to Heathrow taxi, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/windsor-to-heathrow",
    ogTitle: "Windsor to Heathrow Taxi - Fixed Airport Transfers SL4",
    ogDescription:
      "Reliable Windsor and Eton to Heathrow taxis with fixed fares, licensed drivers and live flight tracking. Book online in seconds with CheapCabGrab.",
    serviceName: "CheapCabGrab - Windsor to Heathrow Taxi",
    areaServed: ["Heathrow Airport", "Windsor SL4", "Eton", "Old Windsor", "Datchet"],
    offerCatalogName: "Windsor to Heathrow Taxi Routes",
    offerName: "Windsor to Heathrow Airport fixed-price taxi",
    offerDescription:
      "Fixed-price taxi transfers from Windsor and Eton (SL4) to Heathrow Airport terminals with licensed drivers and flight tracking.",
    reservationName: "Taxi booking from Windsor to Heathrow",
  },
  hero: {
    eyebrow: "Windsor to Heathrow taxi route",
    h1: "Windsor to Heathrow Taxi - Fixed-Price Airport Transfers",
    subtitle: "SL4 airport transfers for Heathrow terminals 2, 3, 4 and 5.",
    body:
      "Book reliable taxis from Windsor, Eton, Datchet and Old Windsor to Heathrow with licensed drivers, flight tracking and secure online booking. Fixed upfront fares with no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Windsor Castle, Windsor SL4",
    presetDropoff: "Heathrow Airport",
  },
  flags: [
    "Fixed fares agreed upfront - no meters",
    "Live flight tracking for Heathrow arrivals",
    "Licensed drivers, meet & greet, luggage-ready",
    "Saloon, MPV, 8-16 seat minibus & coach options",
  ],
  reviewPageKey: "/windsor-to-heathrow",
  reviewAriaLabel: "Windsor to Heathrow customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Windsor to Heathrow taxis",
  floatingCta: "Book Windsor to Heathrow Taxi",
  mobileCta: "Book Windsor to Heathrow Taxi",
  supportNudge: "Need Windsor to Heathrow help?",
  sections: [
    {
      id: "route-overview",
      title: "Windsor to Heathrow at a glance",
      body:
        "A short airport corridor for Windsor residents, Castle visitors, Eton stays, LEGOLAND families and hotel guests heading to Heathrow.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: Approx. 8-12 miles depending on pickup point",
            "Typical time: 15-30 minutes",
            "Typical saloon fare: from GBP40-GBP55",
            "Areas covered: Windsor, Eton, Old Windsor, Datchet and nearby SL4 locations",
          ],
        },
        {
          title: "Great for",
          items: [
            "Windsor residents flying from Heathrow",
            "Windsor Castle and town-centre hotel guests",
            "Families travelling after LEGOLAND Windsor visits",
            "Early flights, late arrivals, bags, buggies and child seats",
          ],
        },
        {
          title: "Why CheapCabGrab?",
          items: [
            "Licensed local drivers with Heathrow and Windsor experience",
            "Transparent fares, no surge pricing",
            "Instant online booking and confirmation",
            "Receipts for business travel and hotel guests",
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Windsor to Heathrow journey",
      body: "Plan the airport transfer, local stay and nearby Windsor attractions with CheapCabGrab tools.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "Heathrow Terminal Finder",
          body: "Not sure which terminal your airline uses? Use our live terminal finder before booking your Windsor airport pickup.",
          link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" },
        },
        {
          title: "Windsor visit planners",
          body: "Staying before or after Heathrow? Compare Windsor Castle and LEGOLAND travel choices, tickets, parking and taxi costs.",
          links: [
            { to: "/windsor-castle-budget-planner", label: "Windsor Castle planner" },
            { to: "/legoland-budget-planner", label: "LEGOLAND planner", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
        {
          title: "Minibus and coach options",
          body: "Travelling as a family group, school party or hotel group from Windsor to Heathrow? Upgrade to a larger vehicle for bags and coordinated pickup.",
          links: [
            { to: "/minibus", label: "Heathrow minibus" },
            { to: "/coach", label: "Heathrow coach", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-areas",
      title: "Windsor to Heathrow - popular pickup and drop-off points",
      body: "We regularly serve homes, hotels, visitor attractions, stations and event locations across Windsor and the wider SL4 area.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "In and around Windsor",
          items: [
            "Windsor Castle, the Long Walk and town centre hotels",
            "Windsor & Eton Central and Windsor & Eton Riverside stations",
            "Eton, Eton College, Datchet and Old Windsor",
            "LEGOLAND Windsor Resort and nearby family hotels",
            "Holiday Inn Express Windsor, Macdonald Windsor and riverside hotels",
          ],
          link: { to: "/windsor-taxi", label: "Windsor taxis overview ->" },
        },
        {
          title: "Heathrow and nearby hotels",
          items: [
            "All Heathrow terminals (T2, T3, T4, T5)",
            "Terminal 5 airport pickups and drop-offs",
            "Airport hotels on Bath Road and Heathrow perimeter roads",
          ],
          link: { to: "/heathrow-hotels", label: "See Heathrow hotel guide ->" },
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Windsor to Heathrow taxi - FAQs",
      body: "A few common questions passengers ask when travelling between Windsor and Heathrow Airport.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["How long does a taxi from Windsor to Heathrow take?", "Most Windsor to Heathrow taxi journeys take around 15-30 minutes, depending on whether you are starting in central Windsor, Eton, Datchet, Old Windsor or near LEGOLAND, plus traffic around the A308, M4 and airport roads."],
            ["Can you collect from Windsor Castle or Windsor hotels?", "Yes. Windsor Castle, the Long Walk, town-centre hotels, riverside hotels and Windsor stations are all regular pickup points. We will confirm the best safe pickup point for your booking."],
            ["Do you track my incoming Heathrow flight?", "Yes. For Heathrow pickups we monitor your flight and adjust the dispatch time where possible. If your flight is severely delayed or diverted, contact support and we will re-plan your pickup."],
            ["Can I book a larger vehicle from Windsor to Heathrow?", "Yes. MPVs, 7-14 seater minibuses and 25+ seater coaches are available for family groups, luggage-heavy trips, school visits and hotel groups. Use the quote form or visit our minibus and coach pages for more details."],
          ].map(([title, body]) => (
            <article className="route-faq-card" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
          <div className="route-faq-footer">
            <p>Need more details? Visit our full <Link to="/faq">FAQ page</Link> or contact support.</p>
          </div>
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Windsor to Heathrow taxi booking?",
      body: "If you have special requirements, multi-stop journeys, child seats, hotel pickups or group travel, our team is happy to help.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>Or send us a message via our <Link to="/contact">contact form</Link> and we will get back to you as soon as possible.</p>
          <p>
            For related Windsor journeys, see our{" "}
            <Link to="/windsor-taxi">Windsor taxi</Link> page,{" "}
            <Link to="/heathrow-to-windsor">Heathrow to Windsor</Link>,{" "}
            <Link to="/windsor-castle-budget-planner">Windsor Castle Budget Planner</Link> and{" "}
            <Link to="/legoland-budget-planner">LEGOLAND Budget Planner</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const WindsorToHeathrow = () => (
  <CcgRoutePremiumPage route={windsorToHeathrowRoute} QuoteComponent={PostcodeQuotenew} />
);

export default WindsorToHeathrow;
